import { Button, ButtonMode, ButtonSize, Checkbox, DateRange, Filter, Icon, ICONS, Loader, Popup, Tooltip, useGlobalModalContext } from '@creditsights/styleguide-web';
import { remove } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import CountryFlag from '../../../components/country-flag/CountryFlag';
import NumberFilter from '../../../components/filter/NumberFilter';
import { MODAL_TYPES } from '../../../components/modal/GlobalModal';
// eslint-disable-next-line import/no-named-as-default
import NoAccessLock from '../../../components/no-access/NoAccessLock';
import { NO_DATA } from '../../../Constants';
import useFetchLoanCompareData from '../../../hooks/loan-compare/useFetchLoanCompareData';
import useFetchLoanCompareFilters from '../../../hooks/loan-compare/useFetchLoanCompareFilters';
import { AccessStatus, LoanComparison } from '../../../models/Securities';
import '../../../scss/filter-tsx.scss';
import { formatDate } from '../../../utils/date.utils';
import { formatResultsText, hasMoreData } from '../../../utils/page.utils';
import { createTagMap } from '../../../utils/tag.utils';
import '../Compare.scss';
import './LoanCompare.scss';

const MAX_SELECTION = 75;

function LoanCompare(): ReactElement {
  const PAGE_SIZE = 100;
  const INITIAL_SIZE_FILTER = { min: null, max: null };
  const INITIAL_DATE_RANGE = { startDate: null, endDate: null, relativeRange: null };
  const navigate = useNavigate();
  const { search, pathname, state } = useLocation();
  const [filterConfig, setFilterConfig] = useState<any>({});
  const [sectorMap, setSectorMap] = useState<any>();
  const [countryMap, setCountryMap] = useState<any>();
  const [sizeFilter, setSizeFilter] = useState(INITIAL_SIZE_FILTER);
  const [dateFilter, setDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const { data: filters, isLoading: filtersLoading } = useFetchLoanCompareFilters();
  const [sort, setSort] = useState({ field: 'borrower', asc: true });
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchLoanCompareData(sort, dateFilter, sizeFilter, filterConfig, PAGE_SIZE);
  const [selections, setSelections] = useState<any[]>([]);
  const { showModal } = useGlobalModalContext();

  useEffect(() => {
    if (search) {
      try {
        const config = JSON.parse(decodeURI(search.substring(1)));
        setSort(config.sort);
        setFilterConfig(config.filterConfig);
        setSizeFilter(config.sizeFilter || {});
        setDateFilter(config.dateFilter || {});
      } catch (e) {
        console.error(e);
      }
    }
    setSelections((state as any)?.selections || []);
  }, []);

  useEffect(() => {
    if (filters) {
      setSectorMap(createTagMap(filters.sectors));
      setCountryMap(createTagMap(filters.country_code));
    }
  }, [filters]);

  useEffect(() => {
    navigate(`${pathname}?${JSON.stringify({ sort, filterConfig, sizeFilter, dateFilter })}`, { replace: true, state: { selections } });
  }, [sort, sizeFilter, filterConfig, dateFilter, selections]);

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config) => {
      // eslint-disable-next-line no-param-reassign
      config[key] = selected;

      if (selected.length === 0) {
        // eslint-disable-next-line no-param-reassign
        delete config[key];
      }

      return { ...config };
    });

    resetSelections();
  };

  const resetFilters = () => {
    setDateFilter({ ...INITIAL_DATE_RANGE });
    setSizeFilter({ ...INITIAL_SIZE_FILTER });
    setFilterConfig({});
    resetSelections();
  };

  const resetSelections = () => {
    setSelections([]);
  };

  const handleDateDateFilterChange = (value) => {
    setDateFilter(value);
    resetSelections();
  };

  const renderFilters = () => {
    return (
      <div className="filters-ctx">
        {Object.keys(filters).map((key) => (
          <Filter key={key} data={filters[key].data} placeholder={filters[key].name} checkedKeys={filterConfig[key] || []} onChange={(checked) => updateFilterConfig(key, checked)} />
        ))}
        <NumberFilter label="Size" min={sizeFilter.min} max={sizeFilter.max} onChange={(e) => setSizeFilter(e)} />
        <DateRange label="Launch Date" startDate={dateFilter.startDate} endDate={dateFilter.endDate} relativeRange={dateFilter.relativeRange} resetInput={0} error={false} setError={() => false} onChange={handleDateDateFilterChange} />
        <div className="separator" />
        <button type="button" className="reset-button" onClick={resetFilters}>
          <Icon name={ICONS.Clear} width={12} height={12} /> Reset
        </button>
      </div>
    );
  };

  const updateSort = (field) => {
    setSort((v) => {
      if (v.field === field) {
        return {
          field,
          asc: !v.asc,
        };
      }
      return {
        field,
        asc: true,
      };
    });
    resetSelections();
  };

  const selectAll = (checked) => {
    if (!checked) {
      setSelections([]);
    } else if (data) {
      const items: any = [];
      for (const page of data.pages) {
        for (const item of page.results) {
          if (!isLocked(item)) {
            items.push(item.id);
          }
        }
      }
      setSelections(items);
    }
  };

  const selectAllProps = () => {
    const props = { checked: false, indeterminate: false };

    if (data) {
      let total = 0;

      for (const page of data.pages) {
        for (const item of page.results) {
          if (!isLocked(item)) {
            total += 1;
          }
        }
      }

      if (selections.length > 0) {
        if (selections.length === total) {
          props.checked = true;
        } else {
          props.indeterminate = true;
        }
      }
    }

    return props;
  };

  const handleSelection = (checked, id) => {
    setSelections((array) => {
      if (checked) {
        array.push(id);
      } else {
        remove(array, (item) => item === id);
      }

      return [...array];
    });
  };

  const showDetails = () => {
    navigate(`/data-tools/loan-compare/details?${JSON.stringify(selections)}`);
  };

  const renderIssuerLink = (loan) => {
    if (loan.cs_universal_id) {
      return (
        <NavLink className="company-name" to={`/companies/${loan.cs_universal_id}`}>
          {loan.borrower}
        </NavLink>
      );
    }
    return <span>{loan.borrower}</span>;
  };

  const renderMultiTags = (tags, map) => {
    if (tags?.length > 0) {
      return tags.map((item) => <div key={item}>{map[item]}</div>);
    }

    return NO_DATA;
  };

  const renderCountryFlag = (security) => {
    if (!security.country_code) {
      return NO_DATA;
    }

    return <CountryFlag countryName={countryMap[security.country_code]} iso3Code={security.country_code} />;
  };

  const renderList = (list) => {
    // eslint-disable-next-line no-param-reassign
    list = list.filter((item) => item.name);
    return (
      <div className="list-ctx">
        <span className="main">{list.slice(0, 1).map((item) => item.name)}</span>
        {list.length > 1 && (
          <Popup
            className="cs-loan-compare-lists"
            basic={false}
            on="click"
            offset={[0, -5]}
            trigger={<span className="trigger">+{list.length - 1}</span>}
            content={
              <div className="list-ctx">
                <table className="pop-up-table">
                  <tbody>
                    {list.map((item, index) => (
                      <tr key={item.name}>
                        <td className="index">{index + 1}</td>
                        <td>{item.name}</td>
                        <td>{item.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
            position="left center"
          />
        )}
      </div>
    );
  };

  const handleNoAccessClick = () => {
    showModal(MODAL_TYPES.UPGRADE_SUBSCRIPTION);
  };

  const isLocked = (security: LoanComparison) => {
    return security.access_status === AccessStatus.subscriptionRequired;
  };

  return (
    <div className="cs-loan-compare-ctx compare-page-list">
      <div className="header-ctx">
        <div className="header">
          <div className="title">Loan Compare Tool</div>
        </div>
      </div>
      <div className="loans-ctx">
        {!filtersLoading && renderFilters()}
        <div className="table-ctx">
          <div className="section-title">
            {data?.pages[0]?.totalResults && (
              <>
                <div className="security-count">{formatResultsText(data)}</div>
                <Tooltip content={`Compare up to ${MAX_SELECTION} Deals at a time`} disabled={selections.length <= MAX_SELECTION}>
                  <div className="actions">
                    <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} onClick={showDetails} disabled={selections.length === 0 || selections.length > MAX_SELECTION}>
                      Compare Selected {selections.length > 0 ? `(${selections.length})` : ''}
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}
          </div>

          {(isLoading || !sectorMap || !countryMap) && (
            <div className="loading-ctx">
              <Loader className="cs-loader" position="center" />
            </div>
          )}
          {!isLoading && sectorMap && countryMap && (
            <>
              <table className="ui table">
                <thead>
                  <tr>
                    <th className="selection">
                      <Checkbox dataTestId="select-all" {...selectAllProps()} onChange={(e, data) => selectAll(data.checked)} />
                    </th>
                    <th className="sortable" onClick={() => updateSort('borrower')}>
                      Borrower <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('launch_date')}>
                      Launch Date <i className="sort icon" />
                    </th>
                    <th className="sortable ui text right aligned" style={{ width: 95 }} onClick={() => updateSort('size')}>
                      Size (mm) <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('purpose')}>
                      Purpose <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('phase')}>
                      Phase <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('sectors')}>
                      Sector/Subsector <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('arrangers.name')}>
                      Arrangers <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('sponsors.name')}>
                      Sponsors <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('region')}>
                      Region <i className="sort icon" />
                    </th>
                    <th className="sortable" onClick={() => updateSort('country_code')}>
                      Country <i className="sort icon" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data &&
                    data.pages.map((page) =>
                      page.results.map((security) => (
                        <tr key={security.id} className={`table-rows ${isLocked(security) ? 'locked' : ''}`} onClick={isLocked(security) ? handleNoAccessClick : undefined}>
                          <td className="checkbox-container">
                            <Checkbox dataTestId={security.id} checked={selections.indexOf(security.id) !== -1} disabled={isLocked(security)} onChange={(e, data) => handleSelection(data.checked, security.id)} />
                          </td>
                          <td>{renderIssuerLink(security)}</td>
                          <td>{formatDate(security.launch_date)}</td>
                          <td className="ui text right aligned">{security.size}</td>
                          <td>{security.purpose}</td>
                          <td>{security.phase}</td>
                          <td>{renderMultiTags(security.sectors, sectorMap)}</td>
                          <td>{renderList(security.arrangers)}</td>
                          <td>{renderList(security.sponsors)}</td>
                          <td>{security.region}</td>
                          <td className="country">
                            {renderCountryFlag(security)}
                            {isLocked(security) && (
                              <div className="no-access-wrapper">
                                <NoAccessLock accessStatus={security.access_status} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                </tbody>
              </table>

              {hasMoreData(data, 100, data?.pages[0]?.totalResults) && (
                <div className="next-page">
                  <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                    Load 100 more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoanCompare;
